/**
 * Per-account feature flags returned by pwa_fetch_my_account_details as
 * `features: [{ feature_key, is_enabled }]`.
 *
 * These are read straight off the API response every time and never persisted.
 * The previous implementation mirrored them into `localStorage.userFeatures`,
 * which meant a paid feature could be unlocked by hand-editing a string in
 * devtools; nothing here writes to storage, so the flag lives only as long as
 * the response that produced it.
 *
 * Client-side gating is still presentation only — the backend has to enforce
 * these same flags on write. This just decides what the UI offers.
 *
 * Keep the key names in sync with the backend feature_flags table.
 */
export const FEATURE_USER_BRANDING = 'user_branding';
export const FEATURE_EVENT_BRANDING = 'event_branding';
export const FEATURE_WATERMARKING = 'branding_watermark';

interface RawFeatureFlag {
  feature_key?: string;
  is_enabled?: unknown;
}

/**
 * The API has sent `is_enabled` as a number (1/0) historically, but a JSON bool
 * or a stringified digit are both plausible from a different backend path, so
 * accept all three and treat anything unrecognised as disabled.
 */
function isEnabledValue(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === '1' || v === 'true';
  }
  return false;
}

/**
 * Normalises the `features` array into a `feature_key -> enabled` map.
 *
 * Returns null — not an empty map — when the response carries no features array
 * at all, so callers can tell "backend has not shipped flags yet" apart from
 * "backend shipped flags and this one is off".
 */
export function parseFeatureFlags(features: unknown): Record<string, boolean> | null {
  if (!Array.isArray(features)) return null;

  const map: Record<string, boolean> = {};
  for (const flag of features as RawFeatureFlag[]) {
    const key = (flag?.feature_key ?? '').toString().trim();
    if (key) {
      map[key] = isEnabledValue(flag?.is_enabled);
    }
  }
  return map;
}

/**
 * Resolves a single feature key off an account response.
 *
 * `fallback` is only consulted when the key is genuinely unavailable — the
 * response has no `features` array, or has one that does not mention this key.
 * That keeps existing users working while the backend rolls the keys out; once
 * a key is present its value wins outright, including when it is disabled.
 */
export function isFeatureEnabled(
  features: unknown,
  key: string,
  fallback: () => boolean
): boolean {
  const map = parseFeatureFlags(features);
  if (!map || !(key in map)) {
    return fallback();
  }
  return map[key];
}
